import React, { useEffect, useState } from 'react';
import { AppErrorBoundary } from './components/ErrorBoundary';
import { AuthGate } from './components/auth/AuthGate';
import { AppLayout, type ModuleKey } from './components/layout/AppLayout';
import { Dashboard } from './modules/dashboard/Dashboard';
import { VorsorgeModul } from './modules/vorsorge/VorsorgeModul';
import { AbrechnungModul } from './modules/abrechnung/AbrechnungModul';
import { KalenderModul } from './modules/kalender/KalenderModul';
import { AkteModul } from './modules/akte/AkteModul';
import { ProtokolleModul } from './modules/protokolle/ProtokolleModul';
import { DokumenteModul } from './modules/dokumente/DokumenteModul';
import { BegehungModul } from './modules/begehung/BegehungModul';
import { AssistentModul } from './modules/assistent/AssistentModul';
import { ZeitModul } from './modules/zeit/ZeitModul';
import { WietzModul } from './modules/wietz/WietzModul';
import { EmailModul } from './modules/email/EmailModul';
import { FormulareModul } from './modules/formulare/FormulareModul';
import { ReportingModul } from './modules/reporting/ReportingModul';
import { AsaModul } from './modules/asa/AsaModul';
import { EinstellungenModul } from './modules/einstellungen/EinstellungenModul';
import { safeOpenDB } from './db/bootstrap';
import { handleOAuthCallback } from './services/googleCalendar';
import { handleDriveOAuthCallback, autoBackupIfNeeded } from './services/googleDriveBackup';
import { handleGmailOAuthCallback } from './services/gmailService';
import { setupAutoBackupOnClose } from './services/autoBackupOnClose';
import { loadSetting } from './services/settingsService';
import { logAktion } from './services/auditLogService';
import { checkAndNotifyFaelligkeiten } from './services/notificationService';
import { loadMandanten } from './services/mandantenService';
import { useMandantStore } from './store/mandantStore';
import { startSyncLoop } from './services/syncService';

interface GCalConfig { clientId: string; calendarId: string; clientSecret?: string; }

type BootState = 'loading' | 'ready' | 'error';

function SwUpdateBanner(): React.JSX.Element | null {
  const [show, setShow] = useState(false);
  const [reg,  setReg]  = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) { return; }
    void navigator.serviceWorker.getRegistration().then(r => {
      if (r === undefined) { return; }
      setReg(r);
      if (r.waiting !== null) { setShow(true); }
      r.addEventListener('updatefound', () => {
        const installing = r.installing;
        if (installing === null) { return; }
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller !== null) {
            setShow(true);
          }
        });
      });
    });
  }, []);

  const handleUpdate = (): void => {
    if (reg?.waiting !== null && reg?.waiting !== undefined) {
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    window.location.reload();
  };

  if (!show) { return null; }
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3
                    bg-blue-700 text-white text-sm px-4 py-3 rounded-xl shadow-lg animate-fade-in">
      <span>🔄 Update verfügbar</span>
      <button onClick={handleUpdate}
        className="bg-white text-blue-700 font-semibold px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors">
        Jetzt neu laden
      </button>
      <button onClick={() => { setShow(false); }}
        className="text-blue-200 hover:text-white ml-1">✕</button>
    </div>
  );
}

function AppShell(): React.JSX.Element {
  const [boot, setBoot]     = useState<BootState>('loading');
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code   = params.get('code');
    const state  = params.get('state');
    const isOAuthCallback = window.location.pathname === '/auth/google/callback'
      && code !== null && state !== null;

    safeOpenDB()
      .then(async () => {
        if (isOAuthCallback) {
          const isDriveCallback = localStorage.getItem('amed_drive_pending') === 'true';
          const isGmailCallback = localStorage.getItem('amed_gmail_pending') === 'true';
          if (isDriveCallback) {
            await handleDriveOAuthCallback(code!, state!);
            window.history.replaceState({}, '', '/?module=einstellungen');
          } else if (isGmailCallback) {
            await handleGmailOAuthCallback(code!, state!);
            window.history.replaceState({}, '', '/?module=einstellungen');
          } else {
            const cfg = await loadSetting<GCalConfig | null>('gcal_config', null);
            if (cfg !== null) {
              await handleOAuthCallback(cfg.clientId, code!, state!, cfg.clientSecret);
            }
            window.history.replaceState({}, '', '/?module=kalender');
          }
        }
        void autoBackupIfNeeded();
        void logAktion('SYSTEM', 'APP_OPENED');
        void checkAndNotifyFaelligkeiten();
        // Mandanten aus Service in Store laden (Single Source of Truth = Service)
        void loadMandanten().then(m => {
          if (m.length > 0) useMandantStore.getState().setMandanten(m);
        });
        // Zentrale-DB-Sync: 60s-Loop + Reconnect (no-op solange nicht angemeldet)
        startSyncLoop();
        setBoot('ready');
      })
      .catch((e: unknown) => {
        setErrMsg(e instanceof Error ? e.message : String(e));
        setBoot('error');
      });
    const onExpired = (): void => { window.location.reload(); };
    window.addEventListener('amed:session-expired', onExpired);
    const cleanupAutoBackup = setupAutoBackupOnClose();
    return () => {
      window.removeEventListener('amed:session-expired', onExpired);
      cleanupAutoBackup();
    };
  }, []);

  if (boot === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-2 border-blue-700 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-gray-500">Datenbank wird initialisiert…</p>
        </div>
      </div>
    );
  }

  if (boot === 'error') {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-white rounded-2xl border border-red-200 shadow p-8 space-y-4">
          <h1 className="text-lg font-semibold text-gray-900">Datenbank-Fehler</h1>
          <p className="text-sm font-mono text-red-800 bg-red-50 rounded p-3 break-all">{errMsg}</p>
          <button onClick={() => { window.location.reload(); }} className="btn-primary w-full justify-center">
            Neu versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <AuthGate>
      <AppLayout>
        {(activeModule: ModuleKey, navigate: (m: ModuleKey) => void) => (
          <ModuleRouter active={activeModule} navigate={navigate} />
        )}
      </AppLayout>
    </AuthGate>
  );
}

function ModuleRouter({ active, navigate }: { active: ModuleKey; navigate: (m: ModuleKey) => void }): React.JSX.Element {
  switch (active) {
    case 'dashboard': return <Dashboard navigate={navigate} />;
    case 'vorsorge':    return <VorsorgeModul />;
    case 'abrechnung':  return <AbrechnungModul />;
    case 'kalender':    return <KalenderModul />;
    case 'akte':        return <AkteModul />;
    case 'protokolle':  return <ProtokolleModul />;
    case 'dokumente':   return <DokumenteModul />;
    case 'begehung':    return <BegehungModul />;
    case 'assistent':   return <AssistentModul />;
    case 'zeit':        return <ZeitModul />;
    case 'wietz':       return <WietzModul />;
    case 'email':       return <EmailModul />;
    case 'formulare':   return <FormulareModul navigate={navigate} />;
    case 'reporting':   return <ReportingModul />;
    case 'asa':         return <AsaModul />;
    case 'einstellungen': return <EinstellungenModul />;
    default:
      return (
        <div className="p-8 text-center text-gray-400">
          <p className="text-4xl mb-3">🚧</p>
          <p className="font-medium text-gray-600">Modul in Entwicklung</p>
          <p className="text-sm mt-1">Folgt in den nächsten Schritten.</p>
        </div>
      );
  }
}

export default function App(): React.JSX.Element {
  return (
    <AppErrorBoundary>
      <AppShell />
      <SwUpdateBanner />
    </AppErrorBoundary>
  );
}
